import type {ReactNode} from 'react';
import {cn} from '@chekinapp/ui';

type Props = {
  title: string;
  isPaying: boolean;
  card: ReactNode;
  amounts?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
};

function PaymentFlowShell({
  title,
  isPaying,
  card,
  amounts,
  children,
  footer,
  className,
}: Props) {
  return (
    <div
      data-slot="payment-flow"
      data-phase={isPaying ? 'pay' : 'form'}
      className={cn(
        'flex h-full min-h-0 flex-col bg-[var(--chekin-color-white)] [container-type:inline-size]',
        className,
      )}
    >
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col',
          '[@container(min-width:720px)]:grid [@container(min-width:720px)]:grid-cols-[296px_minmax(0,1fr)] [@container(min-width:720px)]:grid-rows-[auto_minmax(0,1fr)]',
        )}
      >
        <header
          className={cn(
            'shrink-0 bg-[var(--background-main)] px-4 pr-14 pt-4',
            '[@container(min-width:720px)]:col-span-2 [@container(min-width:720px)]:bg-transparent [@container(min-width:720px)]:px-7 [@container(min-width:720px)]:pb-4 [@container(min-width:720px)]:pt-6',
          )}
        >
          <h2 className="text-[20px] font-semibold leading-[26px] text-[var(--chekin-color-brand-navy)] [@container(min-width:720px)]:text-[22px] [@container(min-width:720px)]:leading-7">
            {title}
          </h2>
        </header>

        <aside
          className={cn(
            'flex shrink-0 flex-col gap-3 bg-[var(--background-main)] px-4 pb-3.5 pt-3',
            '[@container(min-width:720px)]:mb-7 [@container(min-width:720px)]:ml-7 [@container(min-width:720px)]:justify-center [@container(min-width:720px)]:rounded-[18px] [@container(min-width:720px)]:p-3.5',
          )}
        >
          {card}
          {!isPaying && amounts}
        </aside>

        <section className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto p-4 [@container(min-width:720px)]:px-7 [@container(min-width:720px)]:pb-3.5 [@container(min-width:720px)]:pt-0">
            {children}
          </div>
          {footer && (
            <div
              className={cn(
                'flex shrink-0 flex-col gap-2 border-t border-[var(--chekin-divider-subtle)] bg-[var(--chekin-color-white)] px-4 pb-[calc(12px+env(safe-area-inset-bottom,0px))] pt-3.5',
                '[@container(min-width:720px)]:border-t-0 [@container(min-width:720px)]:px-7 [@container(min-width:720px)]:pb-6 [@container(min-width:720px)]:pt-2',
              )}
            >
              {footer}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export {PaymentFlowShell};
